import {createAsyncThunk} from '@reduxjs/toolkit';
import {
    ApprovalRequestApi,
    type CompanySpacesApprovalRequestsBulkApprovePostRequest,
    type CompanySpacesApprovalRequestsEligibleUsersGetRequest,
    type CompanySpacesApprovalRequestsGetRequest,
    type CompanySpacesApprovalRequestsIdApprovePostRequest,
    type CompanySpacesApprovalRequestsIdGetRequest,
    type CompanySpacesApprovalRequestsIdPutRequest,
    type CompanySpacesApprovalRequestsIdRejectPostRequest,
    type CompanySpacesApprovalRequestsPostRequest,
    type CompanySpacesUsersUserIdApprovalRequestsGetRequest,
    type CompanySpacesUsersUserIdApprovalRequestsIdCancelPostRequest,
} from '../generated-api';
import {API_CONFIG} from 'app/api-config';
import {toPagedResult} from '../helpers/api';
import type {ApprovalRequestDetail} from 'model/approval';

const approvalRequestApi = new ApprovalRequestApi(API_CONFIG);

/** Uses the raw variant so the caller also gets the pagination headers. */
export const fetchApprovalRequests = createAsyncThunk('approvalRequest/list',
    async (args: CompanySpacesApprovalRequestsGetRequest) => {
        return await toPagedResult(
            await approvalRequestApi.companySpacesApprovalRequestsGetRaw(args)
        );
    });

/** Uses the raw variant so the caller also gets the pagination headers. */
export const fetchUserApprovalRequests = createAsyncThunk('approvalRequest/listForUser',
    async (args: CompanySpacesUsersUserIdApprovalRequestsGetRequest) => {
        return await toPagedResult(
            await approvalRequestApi.companySpacesUsersUserIdApprovalRequestsGetRaw(args)
        );
    });

/**
 * Withdrawing one's own pending request. It sits under the user endpoint, not the
 * management one -- the policy only lets the record's owner through.
 */
export const cancelUserApprovalRequest = createAsyncThunk('approvalRequest/cancel',
    async (args: CompanySpacesUsersUserIdApprovalRequestsIdCancelPostRequest) => {
        return await approvalRequestApi.companySpacesUsersUserIdApprovalRequestsIdCancelPost(args);
    });

export const fetchApprovalRequest = createAsyncThunk('approvalRequest/get',
    async (args: CompanySpacesApprovalRequestsIdGetRequest) => {
        return (await approvalRequestApi.companySpacesApprovalRequestsIdGet(
            args
        )) as ApprovalRequestDetail;
    });

export const createApprovalRequest = createAsyncThunk('approvalRequest/create',
    async (args: CompanySpacesApprovalRequestsPostRequest) => {
        return await approvalRequestApi.companySpacesApprovalRequestsPost(args);
    });

export const updateApprovalRequest = createAsyncThunk('approvalRequest/update',
    async (args: CompanySpacesApprovalRequestsIdPutRequest) => {
        return await approvalRequestApi.companySpacesApprovalRequestsIdPut(args);
    });

export const fetchApprovalEligibleUsers = createAsyncThunk('approvalRequest/eligibleUsers',
    async (args: CompanySpacesApprovalRequestsEligibleUsersGetRequest) => {
        return await approvalRequestApi.companySpacesApprovalRequestsEligibleUsersGet(args);
    });

/**
 * The only codes a decision endpoint raises to refuse on business grounds: 403
 * from the Pundit policy, and a 400 from `Approve`/`Reject` themselves --
 * `fetchErrorMiddleware` reports the latter under the error body's own code
 * (`ValidationFailedError`), falling back to the bare status when there is none.
 *
 * Anything else -- a 5xx, a dropped connection, an expired session -- is a fault
 * rather than a refusal, and has to keep taking the global route so it still
 * raises a toast and reaches Sentry.
 */
const DECISION_REFUSAL_CODES = ['403', '400', 'VALIDATION_FAILED', 'BAD_REQUEST'];

/**
 * Deciding refuses for reasons the manager has to see and act on: 400 for an
 * already-decided or still-incomplete record, 403 for a requester who has
 * dropped out of the acting user's management scope -- an archived account,
 * whose pending request `List` nevertheless keeps showing to managers.
 *
 * `apiUnautorizedMiddleware` answers a bare 403 by sending the browser to the
 * dashboard, which would throw the manager off the queue mid-decision and take
 * the error message with it. Re-raising a refusal as a rejected *value* keeps it
 * out of both global middlewares (they skip `meta.rejectedWithValue`), so the
 * dialog reports it in place and the screen stays put.
 */
const asDecisionFailure = async (run: () => Promise<unknown>, rejectWithValue: (value: any) => any) => {
    try {
        return await run();
    } catch (e: any) {
        if (!DECISION_REFUSAL_CODES.includes(String(e?.code))) {
            throw e;
        }
        return rejectWithValue(e);
    }
};

export const approveApprovalRequest = createAsyncThunk('approvalRequest/approve',
    async (args: CompanySpacesApprovalRequestsIdApprovePostRequest, {rejectWithValue}) => {
        return await asDecisionFailure(
            () => approvalRequestApi.companySpacesApprovalRequestsIdApprovePost(args), rejectWithValue);
    });

export const rejectApprovalRequest = createAsyncThunk('approvalRequest/reject',
    async (args: CompanySpacesApprovalRequestsIdRejectPostRequest, {rejectWithValue}) => {
        return await asDecisionFailure(
            () => approvalRequestApi.companySpacesApprovalRequestsIdRejectPost(args), rejectWithValue);
    });

/**
 * Partial-success endpoint: every id is re-verified server side, so the result
 * carries both what went through and what did not.
 */
export const bulkApproveApprovalRequests = createAsyncThunk('approvalRequest/bulkApprove',
    async (args: CompanySpacesApprovalRequestsBulkApprovePostRequest, {rejectWithValue}) => {
        return await asDecisionFailure(
            () => approvalRequestApi.companySpacesApprovalRequestsBulkApprovePost(args), rejectWithValue);
    });
